import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api.ts';
import { useSnackbar } from './SnackbarContext.tsx';

interface User {
  id: number;
  email: string;
  type: 'admin' | 'manager';
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

interface AuthContextType {
  user: User | null;
  sessionToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showError, showSuccess } = useSnackbar();

  const isAuthenticated = !!user && !!sessionToken;

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = async () => {
      const storedToken = localStorage.getItem('sessionToken');
      if (storedToken) {
        try {
          const data = await authApi.validate(storedToken);
          setUser(data.user);
          setSessionToken(storedToken);
        } catch (err) {
          localStorage.removeItem('sessionToken');
        }
      }
      setIsLoading(false);
    };

    checkExistingSession();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);

    try {
      const data = await authApi.login({ email, password });
      setUser(data.user);
      setSessionToken(data.sessionToken);
      localStorage.setItem('sessionToken', data.sessionToken);
      showSuccess('Login successful!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      showError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    if (sessionToken) {
      try {
        await authApi.logout(sessionToken);
        showSuccess('Logged out successfully!');
      } catch (err) {
        console.error('Logout error:', err);
        showError('Error during logout');
      }
    }

    setUser(null);
    setSessionToken(null);
    localStorage.removeItem('sessionToken');
  };

  const value: AuthContextType = {
    user,
    sessionToken,
    isAuthenticated,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
